import { CalendarRange } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { EEOCNudgeAllButton } from './EEOCNudgeControls';

const FILTERS = [
    { id: 'all', label: 'Everyone' },
    { id: 'completed', label: 'Completed' },
    { id: 'pending', label: 'Not completed' },
    { id: 'overdue', label: 'Overdue' }
];

export const filterEEOCRows = (rows, filter) => {
    if (filter === 'completed') return rows.filter((r) => r.period_status === 'Completed');
    if (filter === 'pending') return rows.filter((r) => r.has_access && r.period_status !== 'Completed');
    if (filter === 'overdue') return rows.filter((r) => r.overdue);
    return rows;
};

const Tile = ({ label, value, testId, accent }) => (
    <div className={cn('rounded-lg border px-3 py-2', accent)}>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-xl font-semibold tabular-nums" data-testid={testId}>{value}</p>
    </div>
);

export const EEOCReportControls = ({ data, period, onPeriodChange, filter, onFilterChange, department, onNudged }) => {
    const s = data.summary;
    return (
        <div className="mb-5 space-y-4" data-testid="eeoc-report-summary">
            <div className="flex flex-wrap items-center gap-3">
                <CalendarRange className="h-4 w-4 text-sky-600" />
                <Select value={period || data.period.key} onValueChange={onPeriodChange}>
                    <SelectTrigger className="w-64" data-testid="eeoc-period-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {data.available_periods.map((p, i) => (
                            <SelectItem key={p.key} value={p.key} data-testid={`eeoc-period-option-${p.key}`}>{p.label}{i === 0 ? ' (current)' : ''}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="inline-flex rounded-lg border p-1" role="radiogroup">
                    {FILTERS.map((f) => (
                        <button key={f.id} type="button" role="radio" aria-checked={filter === f.id} onClick={() => onFilterChange(f.id)}
                            className={cn('rounded-md px-3 py-1 text-sm font-medium transition-colors', filter === f.id ? 'bg-sky-600 text-white' : 'text-muted-foreground hover:bg-muted')}
                            data-testid={`eeoc-filter-${f.id}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
                <span className="text-xs text-muted-foreground">Refresher: every {data.period.unit} · completed = all {s.total_modules} modules passed in the period</span>
                {data.is_current_period && <div className="ml-auto"><EEOCNudgeAllButton summary={s} department={department} onDone={onNudged} /></div>}
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
                <Tile label={`Completed ${s.period_label}`} value={`${s.period_completed}/${s.users_with_access}`} testId="eeoc-summary-period-completed" accent="bg-emerald-500/5" />
                <Tile label="Not completed" value={s.period_pending} testId="eeoc-summary-period-pending" accent="bg-red-500/5" />
                <Tile label="Overdue now" value={s.overdue ?? 0} testId="eeoc-summary-overdue" accent="border-red-300/60 bg-red-500/10 dark:border-red-800/60" />
                <Tile label="Completion rate" value={`${s.period_completion_rate}%`} testId="eeoc-summary-period-rate" accent="bg-sky-500/5" />
                <Tile label="Sessions (all time)" value={s.total_sessions} testId="eeoc-summary-sessions" accent="bg-sky-500/5" />
                <Tile label="Avg score" value={s.average_score ?? '-'} testId="eeoc-summary-avg-score" accent="bg-sky-500/5" />
                <Tile label="Pass mark" value={s.passing_score} testId="eeoc-summary-passing-score" accent="bg-sky-500/5" />
            </div>
        </div>
    );
};
